import { getTrackingParamsFromUrl, getSavedTrackingParams } from "../utils/utmTracker";

/** Debe coincidir con `STORAGE_KEY` en `utmTracker.js` */
const UTM_TRACKING_STORAGE_KEY = "utm_tracking_params";

export const UTM_DEFAULT_SOURCE = "directo";
export const UTM_DEFAULT_MEDIUM = "landing_mkt";

/**
 * `true` si en la query actual faltan los tres UTMs principales
 * (`utm_source`, `utm_medium`, `utm_campaign`).
 */
export const urlSinUtmsPrincipales = (searchString = typeof window !== "undefined" ? window.location.search : "") => {
  const sp = new URLSearchParams(searchString);
  const has = (k) => {
    const v = sp.get(k);
    return v != null && String(v).trim() !== "";
  };
  return !has("utm_source") && !has("utm_medium") && !has("utm_campaign");
};

const readStoredParams = () => {
  try {
    const raw = sessionStorage.getItem(UTM_TRACKING_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return {};
};

/**
 * Sin UTMs principales en URL → defaults en storage y se limpian `utm_campaign` / `utm_term`.
 * Con UTMs en URL → se persisten en sessionStorage los params presentes en la query (utm_* relevantes).
 */
export const saveUtmParams = (url = typeof window !== "undefined" ? window.location.href : "") => {
  if (typeof window === "undefined") return;
  const urlObj = new URL(url);
  const urlParams = getTrackingParamsFromUrl(url);
  const stored = readStoredParams();
  const merged = { ...stored, ...urlParams };

  if (urlSinUtmsPrincipales(urlObj.search)) {
    merged.utm_source = UTM_DEFAULT_SOURCE;
    merged.utm_medium = UTM_DEFAULT_MEDIUM;
    delete merged.utm_campaign;
    delete merged.utm_term;
  } else {
    const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
    keys.forEach((key) => {
      if (!urlObj.searchParams.has(key)) return;
      const v = urlObj.searchParams.get(key);
      if (v != null && String(v).trim() !== "") {
        merged[key] = v;
      } else {
        delete merged[key];
      }
    });
  }

  sessionStorage.setItem(UTM_TRACKING_STORAGE_KEY, JSON.stringify(merged));
};

/**
 * UTMs canónicos para formulario, CRM y Tecnóm.
 */
export const getUtmParams = () => {
  if (typeof window === "undefined") {
    return {
      utm_source: UTM_DEFAULT_SOURCE,
      utm_medium: UTM_DEFAULT_MEDIUM,
      utm_campaign: "",
      utm_term: "",
    };
  }
  const urlObj = new URL(window.location.href);
  const saved = getSavedTrackingParams();

  if (urlSinUtmsPrincipales(urlObj.search)) {
    return {
      utm_source: UTM_DEFAULT_SOURCE,
      utm_medium: UTM_DEFAULT_MEDIUM,
      utm_campaign: "",
      utm_term: "",
    };
  }

  const pick = (key) => {
    const fromUrl = urlObj.searchParams.get(key);
    if (fromUrl != null && String(fromUrl).trim() !== "") return fromUrl;
    const fromSaved = saved[key];
    if (fromSaved != null && String(fromSaved).trim() !== "") return String(fromSaved);
    return "";
  };

  let utm_source = pick("utm_source") || UTM_DEFAULT_SOURCE;
  let utm_medium = pick("utm_medium") || UTM_DEFAULT_MEDIUM;
  const utm_campaign = pick("utm_campaign") || "";
  const utm_term = pick("utm_term") || "";

  if (!String(utm_source).trim()) utm_source = UTM_DEFAULT_SOURCE;
  if (!String(utm_medium).trim()) utm_medium = UTM_DEFAULT_MEDIUM;

  return { utm_source, utm_medium, utm_campaign, utm_term };
};

/**
 * URL actual con UTMs principales coherentes (defaults o URL + storage).
 * Conserva otros params de tracking guardados (gclid, gad_*, etc.).
 */
export const getCurrentUrlWithUtmDefaults = () => {
  if (typeof window === "undefined") return "";
  const p = getUtmParams();
  const url = new URL(window.location.href);
  url.searchParams.set("utm_source", p.utm_source);
  url.searchParams.set("utm_medium", p.utm_medium);
  if (p.utm_campaign) url.searchParams.set("utm_campaign", p.utm_campaign);
  else url.searchParams.delete("utm_campaign");
  if (p.utm_term) url.searchParams.set("utm_term", p.utm_term);
  else url.searchParams.delete("utm_term");

  const saved = getSavedTrackingParams();
  Object.entries(saved).forEach(([key, value]) => {
    if (["utm_source", "utm_medium", "utm_campaign", "utm_term"].includes(key)) return;
    if (value != null && String(value) !== "") {
      url.searchParams.set(key, String(value));
    }
  });

  return url.toString();
};

/**
 * Query string solo con UTMs canónicos (para enlaces que solo necesitan campaña).
 */
export const buildUtmQueryString = () => {
  const { utm_source, utm_medium, utm_campaign, utm_term } = getUtmParams();
  const parts = [
    `utm_source=${encodeURIComponent(utm_source)}`,
    `utm_medium=${encodeURIComponent(utm_medium)}`,
  ];
  if (utm_campaign) parts.push(`utm_campaign=${encodeURIComponent(utm_campaign)}`);
  if (utm_term) parts.push(`utm_term=${encodeURIComponent(utm_term)}`);
  return `?${parts.join("&")}`;
};
